import images from './images.json';

/**
 * Product config
 *
 * showSkuDescription - Boolean - to show description of selected SkuProduct on ProductDetailPage
 * hideProductConfiguration - Boolean - to hide ProductConfigration component on ProductDetailPage
 * showSkuPriceStartingAt - Boolean - to show label "Starting at" before price on ProductDetailPage
 * variantDescription - object - descriptions for each Category.Value - mainly is used by reducer
 * defaultPrintOnly - Boolean - when true - bundle is not selected by default
 * hidePrintOnly - Boolean - when true - print only is not visible
 * labels - object - hash of custom labels - simply [key]: [value]
 */

/**
 * REDUCER EXAMPLE
 *
  reducer: [
    'configReducer',
    [
      // {
      //   key: 'addressing',
      //   label: 'Addressing',
      //   values: ['none', 'return-only', 'return-recipient'],
      // },
      // {
      //   key: 'color',
      //   label: 'Envelope Color',
      //   values: ['white', 'ivory'],
      // },
    ],
    {
      // none: 'None',
      // white: 'White',
    },
    // {
    //   unavailable: [{ 'configB.Third Option': 'Coming soon' }],
    // },
  ],
 */

const PRODUCT_SPECIFICATION = {
  'photo-card': {
    skus: [
      'photo-card-3',
      'photo-card-9',
      'photo-card-10',
      'photo-card-11',
      'photo-card-12',
      'photo-card-13',
    ],
    upsell: [
      {
        skus: ['envelope-7x5-white-blank', 'envelope-7x5-oatmeal-blank'],
        label: 'No Address',
        recommended: false,
      },
      {
        skus: ['envelope-7x5-white-address'],
        label: 'With Addressing',
        recommended: true,
      },
    ],
    images: images['photo-card-nonfoil'],
    config: {
      defaultPrintOnly: false,
      hidePrintOnly: true,
      labels: { color: 'Envelope Color' },
    },
  },

  'photo-card-foil': {
    skus: [
      'photo-card-1-foil',
      'photo-card-2-foil',
      'photo-card-4-foil',
      'photo-card-5-foil',
      'photo-card-6-foil',
      'photo-card-7-foil',
    ],
    upsell: [
      {
        skus: ['envelope-7x5-white-blank', 'envelope-7x5-oatmeal-blank'],
        label: 'No Address',
        recommended: false,
      },
      {
        skus: ['envelope-7x5-white-address'],
        label: 'With Addressing',
        recommended: true,
      },
    ],
    images: images['photo-card-foil'],
    config: {
      defaultPrintOnly: false,
      hidePrintOnly: true,
      labels: { color: 'Envelope Color' },
    },
  },

  'photo-greeting-card': {
    skus: [
      'photo-card-115',
      'photo-card-116',
      'photo-card-117',
      'photo-card-118',
      'photo-card-119',
      'photo-card-120',
    ],
    upsell: [
      {
        skus: ['envelope-7x5-white-blank', 'envelope-7x5-oatmeal-blank'],
        label: 'No Address',
        recommended: false,
      },
      {
        skus: ['envelope-7x5-white-address'],
        label: 'With Addressing',
        recommended: true,
      },
    ],
    images: images['photo-greeting-card'],
    config: {
      defaultPrintOnly: false,
      hidePrintOnly: true,
      labels: { color: 'Envelope Color' },
    },
  },

  'photo-greeting-card-foil': {
    skus: [
      'photo-card-133-foil',
      'photo-card-134-foil',
      'photo-card-135-foil',
      'photo-card-140-foil',
      'photo-card-146-foil',
    ],
    upsell: [
      {
        skus: ['envelope-7x5-white-blank', 'envelope-7x5-oatmeal-blank'],
        label: 'No Address',
        recommended: false,
      },
      {
        skus: ['envelope-7x5-white-address'],
        label: 'With Addressing',
        recommended: true,
      },
    ],
    images: images['photo-greeting-card'],
    config: {
      defaultPrintOnly: false,
      hidePrintOnly: true,
      labels: { color: 'Envelope Color' },
    },
  },

  artprint: {
    url: '/artprint',
    reducer: [
      'productIdReducer',
      'artprint-(.*)-(.*)',
      [
        { key: 'size', label: 'Size', index: 1 },
        { key: 'finish', label: 'Finish', index: 0 },
      ],
      {
        '': 'Matte',
        satin: 'Satin',
        '8x10': '8x10”',
        '11x14': '11x14”',
        small: '12x17”',
        large: '16x20”',
        '20x24': '20x24”',
      },
    ],
    skus: [
      'artprint-8x10',
      'artprint-11x14',
      'artprint-small',
      'artprint-large',
      'artprint-20x24',
      'artprint-8x10-satin',
      'artprint-11x14-satin',
      'artprint-small-satin',
      'artprint-large-satin',
      'artprint-20x24-satin',
    ],
    bundles: [
      'bundle-artprint-11x14',
      'bundle-artprint-small',
      'bundle-artprint-large',
      'bundle-artprint-20-24',
    ],
    images: images.artprint,
    config: { defaultPrintOnly: true },
  },

  'classic-prints': {
    url: '/classic-prints',
    skus: ['recprints', 'recprints-large'],
    bundles: ['bundle-recprints', 'bundle-recprints-large'],
    images: images['classic-prints'],
    config: { defaultPrintOnly: true },
  },

  'clip-lights': {
    url: '/clip-lights',
    skus: ['clip-lights'],
    images: images['clip-lights'],
  },

  'premium-hardcover-books': {
    url: '/premium-hardcover-books',
    reducer: [
      'productIdReducer',
      'book-(.*)-hardcover-(.*)',
      [
        { key: 'size', label: 'Size' },
        { key: 'cover', label: 'Cover' },
      ],
      {
        'leather-black': 'Leather Black',
        'leather-tan': 'Leather Tan',
        // 'linen-gray': 'Linen Gray',
        // 'linen-black': 'Linen Black',
        // 'linen-cream': 'Linen Cream',
        custom: 'Custom',
        '11x8': '11x8.5”',
        '13x10': '13x10”',
      },
      {
        availableOnly: true,
      },
    ],
    skus: [
      'book-11x8-hardcover-custom',
      // 'book-11x8-hardcover-linen-gray',
      // 'book-11x8-hardcover-linen-black',
      // 'book-11x8-hardcover-linen-cream',
      'book-11x8-hardcover-leather-black',
      'book-11x8-hardcover-leather-tan',
      'book-13x10-hardcover-custom',
      // 'book-13x10-hardcover-linen-gray',
      // 'book-13x10-hardcover-linen-black',
      // 'book-13x10-hardcover-linen-cream',
      'book-13x10-hardcover-leather-black',
      'book-13x10-hardcover-leather-tan',
    ],
    images: images['premium-hardcover-books'],
  },

  'custom-books': {
    url: '/custom-books',
    reducer: [
      'productIdReducer',
      'book-8x8-(.*)-custom',
      [{ key: 'type', label: 'Type' }],
      {},
      {
        availableOnly: true,
      },
    ],
    skus: ['book-8x8-hardcover-custom', 'book-8x8-softcover-custom'],
    images: images['softcover-books'],
  },

  'custom-layflats': {
    url: '/custom-layflats',
    reducer: [
      'productIdReducer',
      'book-(.*)-layflat-custom',
      [{ key: 'size', label: 'Size' }],
      {
        '8x8': '8x8”',
        '11x8': '11x8.5”',
      },
      {
        availableOnly: true,
      },
    ],
    skus: ['book-8x8-layflat-custom', 'book-11x8-layflat-custom'],
    images: images['hardcover-book'],
  },

  // 'desktop-calendar': {
  //   url: '/desktop-calendar',
  //   skus: ['desktop-calendar'],
  //   images: images['desktop-calendar'],
  // },

  // 'desktop-calendar-flip': {
  //   url: '/desktop-calendar-flip',
  //   skus: ['desktop-calendar-flip'],
  //   images: images['desktop-calendar-flip'],
  // },

  'engineer-prints': {
    url: '/engineer-prints',
    skus: ['engineer-print'],
    bundles: ['bundle-engineer-print'],
    images: images['engineer-prints'],
  },

  'engineer-print-calendar': {
    url: '/engineer-print-calendar',
    skus: ['engineer-print-calendar'],
    bundles: ['bundle-engineer-print-calendar'],
    images: images['engineer-print-calendar'],
  },

  // 'foil-bordered-square-prints': {
  //   url: '/foil-bordered-square-prints',
  //   reducer: [
  //     'productIdReducer',
  //     '(.*)-square-(.*)',
  //     ['Border Color', 'Size'],
  //     { small: '4”', large: '5.5”' },
  //   ],
  //   skus: ['silver-square-small', 'silver-square-large', 'gold-square-small', 'gold-square-large'],
  //   images: images['foil-bordered-square-prints'],
  // },

  'framed-prints': {
    url: '/framed-canvas-prints',
    reducer: ['productIdReducer', 'framed_canvas_(.*)_(.*)', ['Size', 'Color']],
    skus: [
      'framed_canvas_8x10_black',
      'framed_canvas_8x10_maple',
      'framed_canvas_8x10_oak',
      'framed_canvas_10x10_black',
      'framed_canvas_10x10_maple',
      'framed_canvas_10x10_oak',
      'framed_canvas_11x14_black',
      'framed_canvas_11x14_maple',
      'framed_canvas_11x14_oak',
      'framed_canvas_16x16_black',
      'framed_canvas_16x16_maple',
      'framed_canvas_16x16_oak',
      'framed_canvas_16x20_black',
      'framed_canvas_16x20_maple',
      'framed_canvas_16x20_oak',
      'framed_canvas_20x30_black',
      'framed_canvas_20x30_maple',
      'framed_canvas_20x30_oak',
      'framed_canvas_24x24_black',
      'framed_canvas_24x24_maple',
      'framed_canvas_24x24_oak',
    ],
    images: images['framed-prints'],
  },

  'framed-art-prints': {
    url: '/framed-art-prints',
    reducer: [
      'productIdReducer',
      'framedprint-(.*)-(.*)-black',
      ['Frame Size', 'Print Size'],
      {
        // hack - f_ has to have "×" this char in ALIAS !!!
        f_11x14: '11×14”',
        f_16x20: '16×20”',
        f_20x30: '20×30”',
        // hack - p_ has to have "x" this char in ALIAS !!!
        p_8x10: '8x10”',
        p_11x14: '11x14”',
        p_12x18: '12x18”',
        p_16x20: '16x20”',
        p_20x30: '20x30”',
      },
      {
        availableOnly: true,
      },
    ],
    skus: [
      'framedprint-f_11x14-p_8x10-black',
      'framedprint-f_11x14-p_11x14-black',
      'framedprint-f_16x20-p_11x14-black',
      'framedprint-f_16x20-p_12x18-black',
      'framedprint-f_16x20-p_16x20-black',
      'framedprint-f_20x30-p_20x30-black',
    ],
    images: images.framedprints,
    config: { showSkuDescription: true },
  },

  // 'framed-art-prints': {
  //   url: '/framed-art-prints',
  //   reducer: ['productIdReducer', 'framed-(.*)-(.*)', ['Size', 'Color']],
  //   skus: [
  //     'framed-5x7-black',
  //     'framed-5x7-maple',
  //     'framed-5x7-oak',
  //     'framed-5x7-white',
  //     'framed-8x10-black',
  //     'framed-8x10-maple',
  //     'framed-8x10-oak',
  //     'framed-8x10-white',
  //   ],
  //   images: images['framed-art-prints'],
  // },

  giftcard: {
    url: '/giftcard',
    skus: ['giftcard-10', 'giftcard-25', 'giftcard-50', 'giftcard-75', 'giftcard-100'],
    images: images['giftcard-pbo'],
  },

  'gold-square-frame': {
    url: '/gold-square-frame',
    skus: ['gold-square-frame'],
    images: images['gold-square-frame'],
  },

  'good-hangups': {
    url: '/good-hangups',
    skus: ['good-hangups'],
    images: images['good-hangups'],
  },

  'framed-photo-grid': {
    url: '/framed-photo-grid',
    skus: ['grid-print-4', 'grid-print-9', 'grid-print-12'],
    images: images['grid-print'],
  },

  'hardcover-book': {
    url: '/hardcover-book',
    skus: ['photolane-hardcover-photo-book'],
    images: images['hardcover-book'],
  },

  'metal-desk-prints': {
    url: '/metal-desk-prints',
    reducer: ['productIdReducer', 'metal-print-(.*)-desk', ['Size']],
    skus: [
      'metal-print-4x4-desk',
      'metal-print-4x6-desk',
      'metal-print-5x5-desk',
      'metal-print-5x7-desk',
      'metal-print-8x10-desk',
    ],
    images: images['metal-desk-prints'],
  },

  'metal-wall-prints': {
    url: '/metal-wall-prints',
    reducer: ['productIdReducer', 'metal-print-(.*)', ['Size']],
    skus: [
      'metal-print-8x10',
      'metal-print-11x14',
      'metal-print-12x12',
      'metal-print-12x18',
      'metal-print-16x20',
      'metal-print-16x24',
      'metal-print-20x30',
      'metal-print-24x36',
    ],
    images: images['metal-wall-prints'],
  },

  // 'instagram-yearbook': {
  //   url: '/instagram-yearbook',
  //   skus: ['instagram-yearbook-small', 'instagram-yearbook-large'],
  //   images: images['instagram-yearbook'],
  // },

  'news-print': {
    url: '/news-print',
    skus: ['news-print'],
    images: images['news-print'],
  },

  'photo-ropes': {
    url: '/photo-ropes',
    skus: ['horizontal-magnetic-rope', 'magnetic-rope'],
    images: images['photo-ropes'],
  },

  'skeleton-clips': {
    url: '/skeleton-clips',
    skus: ['skeleton-clip-small', 'skeleton-clip-large'],
    images: images['skeleton-clips'],
  },

  'softcover-books': {
    url: '/softcover-books',
    skus: ['softcover-photo-book', 'photolane-softcover-photo-book'],
    images: images['softcover-books'],
  },

  'square-engineer-prints': {
    url: '/square-engineer-prints',
    skus: ['square-engineer-print'],
    bundles: ['bundle-square-engineer-print'],
    images: images['square-engineer-prints'],
  },

  'square-envelopes': {
    url: '/square-envelopes',
    skus: ['square-envelopes-small', 'square-envelopes-large'],
    images: images['square-envelopes'],
  },

  'square-prints': {
    url: '/square-prints',
    skus: ['square-print-small', 'square-print'],
    bundles: [
      'bundle-square-prints-small',
      'bundle-square-prints',
      'bundle-square-prints-small-album',
      'bundle-square-prints-album',
    ],
    images: images['square-prints'],
    config: { defaultPrintOnly: true },
  },

  'square-print-single': {
    url: '/square-print-single',
    skus: ['square-print-small', 'square-print'],
    images: images['square-print-single'],
  },

  'square-print-photo-album': {
    url: '/square-print-photo-album',
    skus: ['square-print-album'],
    images: images['square-print-album'],
  },

  'tiny-square-prints': {
    url: '/tiny-square-prints',
    skus: ['tiny-square-prints'],
    images: images['tiny-square-prints'],
    config: { defaultPrintOnly: true },
  },

  'wall-calendar': {
    url: '/wall-calendar',
    skus: ['wall-calendar'],
    images: images['wall-calendar'],
  },

  'wall-calendar-large': {
    url: '/wall-calendar-large',
    skus: ['wall-calendar-large'],
    images: images['wall-calendar-large'],
  },

  'wall-calendar-new': {
    url: '/wall-calendar-new',
    skus: ['wall-calendar-new'],
    images: images['ancestry-calendar'],
  },

  'washi-tape': {
    url: '/washi-tape',
    skus: ['washi-tape-gold', 'washi-tape-rainbow', 'washi-tape'],
    images: images['washi-tape'],
  },

  'wood-block': {
    url: '/wood-block',
    skus: ['wood-block'],
    images: images['wood-block'],
  },

  'wooden-rails': {
    url: '/wooden-rails',
    reducer: [
      'productIdReducer',
      'wooden-rail-(.*)-(.*)',
      [
        { key: 'size', label: 'Size' },
        { key: 'color', label: 'Color' },
      ],
      {
        12: '12”',
        14: '14”',
        16: '16”',
        17: '17”',
        20: '20”',
        24: '24”',
        36: '36”',
        48: '48”',
      },
      {
        availableOnly: true,
      },
    ],
    skus: [
      'wooden-rail-12-black',
      'wooden-rail-12-maple',
      'wooden-rail-12-oak',
      'wooden-rail-14-black',
      'wooden-rail-14-maple',
      'wooden-rail-14-oak',
      'wooden-rail-16-black',
      'wooden-rail-16-maple',
      'wooden-rail-16-oak',
      'wooden-rail-17-black',
      'wooden-rail-17-maple',
      'wooden-rail-17-oak',
      'wooden-rail-20-black',
      'wooden-rail-20-maple',
      'wooden-rail-20-oak',
      'wooden-rail-24-black',
      'wooden-rail-24-maple',
      'wooden-rail-24-oak',
      'wooden-rail-36-black',
      'wooden-rail-36-maple',
      'wooden-rail-36-oak',
      'wooden-rail-48-black',
      'wooden-rail-48-maple',
      'wooden-rail-48-oak',
    ],
    images: images['wooden-rails'],
  },

  'wrapped-canvas-prints': {
    url: '/wrapped-canvas-prints',
    reducer: ['productIdReducer', 'gallery-wrapped-canvas-(.*)', ['Size']],
    skus: [
      'gallery-wrapped-canvas-8x8',
      'gallery-wrapped-canvas-8x10',
      'gallery-wrapped-canvas-11x14',
      'gallery-wrapped-canvas-12x12',
      'gallery-wrapped-canvas-12x36',
      'gallery-wrapped-canvas-16x16',
      'gallery-wrapped-canvas-16x20',
      'gallery-wrapped-canvas-16x24',
      'gallery-wrapped-canvas-20x24',
      'gallery-wrapped-canvas-20x30',
      'gallery-wrapped-canvas-20x60',
      'gallery-wrapped-canvas-24x36',
      'gallery-wrapped-canvas-30x40',
      'gallery-wrapped-canvas-36x36',
    ],
    images: images['wrapped-canvas-prints'],
  },

  'acrylic-prints': {
    url: '/acrylic-prints',
    reducer: ['productIdReducer', 'acrylic-print-(.*)', ['Size']],
    skus: [
      'acrylic-print-8x10',
      'acrylic-print-11x14',
      'acrylic-print-12x12',
      'acrylic-print-12x18',
      'acrylic-print-16x20',
      'acrylic-print-16x24',
      'acrylic-print-20x20',
      'acrylic-print-20x30',
      'acrylic-print-24x36',
      'acrylic-print-30x40',
    ],
    images: images['acrylic-wallprint'],
  },

  'acrylic-magnets': {
    url: '/acrylic-magnets',
    skus: [
      'acrylic-magnet-square-small',
    ],
    images: images['acrylic-magnet']
  },

  'photobooth-magnets': {
    url: '/photobooth-magnets',
    skus: [
      'photobooth-magnet-1',
      'photobooth-magnet-2',
      'photobooth-magnet-4',
    ],
    images: images['photobooth-magnet'],
  },

  yearbook: {
    url: '/yearbook',
    skus: ['yearbook-small', 'yearbook-large'],
    images: images.yearbook,
  },
};

export default PRODUCT_SPECIFICATION;
